import { motion, useInView } from "framer-motion";
import { useEffect, useRef, useState } from "react";

export function Section({ id, children, className = "" }) {
  return (
    <section id={id} className={`relative py-20 sm:py-24 md:py-32 ${className}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">{children}</div>
    </section>
  );
}

export function Eyebrow({ children }) {
  return <div className="text-xs uppercase tracking-[0.2em] text-gold mb-4">{children}</div>;
}

export function H2({ children, className = "" }) {
  return (
    <h2 className={`font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium leading-[1.05] text-white ${className}`}>
      {children}
    </h2>
  );
}

export function Lead({ children, className = "" }) {
  return <p className={`text-base sm:text-lg text-white/70 max-w-2xl leading-relaxed ${className}`}>{children}</p>;
}

export function GlassCard({ children, className = "", ...rest }) {
  return (
    <div className={`bg-[#101013] border border-white/10 transition-all hover:border-gold/40 hover:-translate-y-1 ${className}`} {...rest}>
      {children}
    </div>
  );
}

export function Reveal({ children, delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

export function Counter({ value, suffix = "", duration = 2000, label, testId }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.floor(eased * value));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, duration]);

  return (
    <div ref={ref} className="border-l border-white/10 pl-6" data-testid={testId}>
      <div className="font-display text-4xl sm:text-5xl tracking-tight text-white">
        {n.toLocaleString()}<span className="text-gold">{suffix}</span>
      </div>
      <div className="mt-2 text-xs uppercase tracking-[0.2em] text-white/50">{label}</div>
    </div>
  );
}

export function StatBar({ items }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-y-10 gap-x-8">
      {items.map((it) => (
        <Counter key={it.label} {...it} />
      ))}
    </div>
  );
}
